/**
 * The system's health, as `GET /v1/admin/health/detailed` answers it, and the
 * German the Verwaltung panel says it in.
 *
 * The gateway's own `message` is deliberately factual English written for an
 * operator's log ("pool saturated: 40/40 in use"). The words a person reads on
 * this screen are ours, derived from `status` and the numeric metrics; the
 * gateway's sentence is shown only underneath a component that is *not*
 * healthy, where the cause matters more than the register.
 *
 * Types are declared here rather than imported from `src/admin/types/health.ts`:
 * that file belongs to the admin build being retired and still declares a
 * `firebase` component the gateway no longer reports.
 */

export type HealthStatus = 'healthy' | 'degraded' | 'unhealthy' | 'unknown'

export interface ComponentHealth {
  status: HealthStatus
  message?: string
  checked_at: string
  duration_ms: number
  metrics?: Record<string, unknown>
}

export interface SystemHealth {
  status: 'healthy' | 'degraded' | 'unhealthy'
  gateway: ComponentHealth
  database: ComponentHealth
  redis: ComponentHealth
  workers: ComponentHealth
  converter: ComponentHealth
  storage: ComponentHealth
  ml_service: ComponentHealth
  timestamp: string
}

/** The seven components, in the order the artboard lists them. */
export const HEALTH_COMPONENTS = [
  { key: 'gateway', name: 'Gateway' },
  { key: 'database', name: 'Datenbank' },
  { key: 'redis', name: 'Warteschlange' },
  { key: 'workers', name: 'Auswertung' },
  { key: 'converter', name: 'PDF-Umwandlung' },
  { key: 'storage', name: 'Dateispeicher' },
  { key: 'ml_service', name: 'Erkennungsdienst' },
] as const

export type HealthComponentKey = (typeof HEALTH_COMPONENTS)[number]['key']

/**
 * Narrows whatever arrived to one of the four states.
 *
 * Anything the gateway invents later — or an empty string — reads as
 * `unknown`, never as healthy: a status this build cannot interpret is a
 * status it must not paint green.
 */
export function componentStatus(raw: unknown): HealthStatus {
  return raw === 'healthy' || raw === 'degraded' || raw === 'unhealthy' || raw === 'unknown'
    ? raw
    : 'unknown'
}

/** The component under `key`, or a stand-in for one the answer omits. A
 *  missing component is unknown — it is not a component that is fine. */
export function componentOf(
  health: SystemHealth | undefined,
  key: HealthComponentKey,
): ComponentHealth | undefined {
  const value = health ? (health as unknown as Record<string, unknown>)[key] : undefined
  if (!value || typeof value !== 'object') return undefined
  return value as ComponentHealth
}

export const STATUS_WORD: Record<HealthStatus, string> = {
  healthy: 'in Ordnung',
  degraded: 'eingeschränkt',
  unhealthy: 'gestört',
  unknown: 'nicht prüfbar',
}

export const STATUS_TONE: Record<HealthStatus, { icon: string; className: string }> = {
  healthy: { icon: 'check_circle', className: 'text-success-text' },
  degraded: { icon: 'schedule', className: 'text-warning-text' },
  unhealthy: { icon: 'error', className: 'text-danger-text' },
  unknown: { icon: 'help', className: 'text-text-muted' },
}

function num(metrics: Record<string, unknown> | undefined, key: string): number | undefined {
  const value = metrics?.[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function str(metrics: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = metrics?.[key]
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

/** Seconds of uptime as the artboard reads it: "6 Tage 4 Std.". */
export function formatUptime(seconds: number): string {
  if (seconds < 60) return `${Math.max(0, Math.round(seconds))} Sek.`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return plural(minutes, 'Minute', 'Minuten')
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${plural(hours, 'Stunde', 'Stunden')} ${minutes % 60} Min.`
  const days = Math.floor(hours / 24)
  return `${plural(days, 'Tag', 'Tagen')} ${hours % 24} Std.`
}

/**
 * The factual half of a component's line, built only from metrics that are
 * actually present. A key the gateway omits produces no words at all — the
 * alternative would be a zero that reads like a measurement.
 */
export function componentDetail(
  key: HealthComponentKey,
  component: ComponentHealth | undefined,
): string {
  const m = component?.metrics
  const parts: string[] = []
  switch (key) {
    case 'gateway': {
      const uptime = num(m, 'uptime_seconds')
      if (uptime !== undefined) parts.push(`seit ${formatUptime(uptime)} ohne Neustart`)
      break
    }
    case 'database': {
      const inUse = num(m, 'connections_in_use')
      const max = num(m, 'connections_max')
      if (inUse !== undefined && max !== undefined) parts.push(`${inUse} von ${max} Verbindungen belegt`)
      else if (inUse !== undefined) parts.push(`${inUse} Verbindungen belegt`)
      break
    }
    case 'redis': {
      const depth = num(m, 'queue_depth')
      if (depth !== undefined) parts.push(`${plural(depth, 'Auftrag', 'Aufträge')} wartend`)
      break
    }
    case 'workers': {
      const active = num(m, 'active_workers')
      const processing = num(m, 'processing_jobs')
      const pending = num(m, 'pending_jobs')
      if (active !== undefined) parts.push(`${active} aktiv`)
      if (processing !== undefined) parts.push(`${processing} in Arbeit`)
      if (pending !== undefined) parts.push(`${pending} wartend`)
      break
    }
    case 'converter': {
      const active = num(m, 'active_converters')
      if (active !== undefined) parts.push(`${active} aktiv`)
      break
    }
    case 'storage': {
      const backend = str(m, 'backend')
      if (backend) parts.push(backend)
      break
    }
    case 'ml_service': {
      const url = str(m, 'url')
      if (url) parts.push(url)
      const code = num(m, 'status_code')
      if (code !== undefined) parts.push(`HTTP ${code}`)
      break
    }
  }
  return parts.join(' · ')
}

/** The whole line under a component's name: our status word, then the facts. */
export function componentLine(
  key: HealthComponentKey,
  component: ComponentHealth | undefined,
): string {
  if (!component) return 'meldet sich nicht — dieses System liefert dazu keine Angabe'
  const status = componentStatus(component.status)
  const detail = componentDetail(key, component)
  return detail ? `${STATUS_WORD[status]} · ${detail}` : STATUS_WORD[status]
}

export interface OverallSummary {
  status: HealthStatus
  /** The headline word, in bold on the banner. */
  word: string
  /** The sentence after it. */
  detail: string
}

const OVERALL_WORD: Record<HealthStatus, string> = {
  healthy: 'Alles in Ordnung',
  degraded: 'Eingeschränkt',
  unhealthy: 'Gestört',
  unknown: 'Nicht beurteilbar',
}

function enumerate(names: string[]): string {
  if (names.length <= 1) return names.join('')
  return `${names.slice(0, -1).join(', ')} und ${names[names.length - 1]}`
}

/**
 * The banner.
 *
 * The gateway states the overall status itself, and that answer wins where it
 * is the worse one. But a response that says "healthy" while a component says
 * anything else is not something to render as good news, so the components are
 * checked too and the worse of the two readings is shown.
 */
export function overallSummary(health: SystemHealth | undefined): OverallSummary {
  if (!health) {
    return {
      status: 'unknown',
      word: OVERALL_WORD.unknown,
      detail: 'Der Systemzustand konnte nicht abgerufen werden.',
    }
  }

  const troubled = HEALTH_COMPONENTS.filter(
    ({ key }) => componentStatus(componentOf(health, key)?.status) !== 'healthy',
  )
  const missing = HEALTH_COMPONENTS.filter(({ key }) => componentOf(health, key) === undefined)

  const reported = componentStatus(health.status)
  const worst: HealthStatus = troubled.some(
    ({ key }) => componentStatus(componentOf(health, key)?.status) === 'unhealthy',
  )
    ? 'unhealthy'
    : troubled.length > 0
      ? 'degraded'
      : 'healthy'
  const status = rank(reported) >= rank(worst) ? reported : worst

  if (troubled.length === 0 && status === 'healthy') {
    return { status, word: OVERALL_WORD.healthy, detail: 'Alle Komponenten antworten.' }
  }

  const names = troubled.map(({ name }) => name)
  const detail =
    missing.length === troubled.length && missing.length > 0
      ? `Zu ${enumerate(names)} liefert das System keine Angabe.`
      : `${enumerate(names)} ${names.length === 1 ? 'meldet' : 'melden'} ein Problem.`
  return { status, word: OVERALL_WORD[status], detail }
}

/** How bad a status is, so two readings can be compared. */
function rank(status: HealthStatus): number {
  return status === 'unhealthy' ? 3 : status === 'degraded' || status === 'unknown' ? 2 : 1
}

/** "Stand: 24.09.2026, 11:21 Uhr" — the instant the gateway stamped, not ours. */
export function formatStand(timestamp: string | undefined): string | null {
  if (!timestamp) return null
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return null
  const text = new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
  return `Stand: ${text} Uhr`
}
